import { http } from '@/apis/aipass'
import { globalHotelId } from '@/hooks/use-hotel-id'
import { env } from '@/libs/env'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { normalizeSpaceTitle, SpaceReservationDetail } from './space-reservation-types'

type DetailApiRow = Omit<SpaceReservationDetail, 'hotelName' | 'spaceTitle' | 'hasQrCode' | 'universalAccessKeyUrl'> & {
  hotelName?: string
  spaceTitle?: SpaceReservationDetail['spaceTitle']
  hasQrCode?: boolean | null
  has_qr_code?: boolean | null
  universalAccessKeyUrl?: string | null
  universal_access_key_url?: string | null
}

function normalizeTrimmedStringOrNull(v: unknown): string | null {
  if (v == null) return null
  if (typeof v !== 'string') return null
  const s = v.trim()
  return s === '' ? null : s
}

export const fetchSpaceReservationDetail = createAsyncThunk<
  SpaceReservationDetail,
  string,
  { rejectValue: unknown }
>(
  'space/fetchSpaceReservationDetail',
  async (reservationId: string, { rejectWithValue }) => {
    try {
      const response = await http<DetailApiRow>({
        method: 'get',
        url: `${env('BASE_API_V3')}/guest/spaces/reservations/${reservationId}`,
        params: { hotelId: globalHotelId() },
      })
      const d = response.data
      const universalAccessKeyUrl = normalizeTrimmedStringOrNull(
        d.universalAccessKeyUrl ?? d.universal_access_key_url
      )
      return {
        ...d,
        hotelName: d.hotelName ?? '',
        spaceTitle: normalizeSpaceTitle(d.spaceTitle),
        hasQrCode: (d.hasQrCode ?? d.has_qr_code) === true,
        universalAccessKeyUrl,
      }
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

const spaceReservationDetailSlice = createSlice({
  name: 'spaceReservationDetail',
  initialState: {
    reservation: null as SpaceReservationDetail | null,
    loading: false,
    error: null as unknown | null,
  },
  reducers: {
    clearReservation: (state) => {
      state.reservation = null
      state.error = null
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchSpaceReservationDetail.pending, (state) => {
      state.loading = true
      state.error = null
    })
    builder.addCase(fetchSpaceReservationDetail.fulfilled, (state, action) => {
      state.reservation = action.payload
      state.loading = false
      state.error = null
    })
    builder.addCase(fetchSpaceReservationDetail.rejected, (state, action) => {
      state.loading = false
      state.error = action.payload ?? null
    })
  },
})

export const { clearReservation } = spaceReservationDetailSlice.actions
export const spaceReservationDetailReducer = spaceReservationDetailSlice.reducer
